import React from 'react';
import PropTypes from 'prop-types';
import { Button, Modal } from 'react-bootstrap';
import styled from 'styled-components';
import {
  COR_FORA_TMS,
  COR_LINHA_CANCELADA,
  COR_LINHA_GESTOR_AVISADO,
  COR_LINHA_INCOMPLETA,
  COR_LINHA_PROTOCOLO,
  COR_LINHA_TERCEIRO,
  COR_LINHA_VX_VALIDA,
  CARROCERIAS,
  PLOTAGENS,
  SeloCarroceria,
  SeloPlotagem,
  SeloTecnologia,
  STATUS_PATIO,
  TagStatusPatio
} from './tanStackColumns';
import { TECNOLOGIAS_COM_LOGO } from '../utils/tecnologiaRastreador';

// Na ordem de prioridade de corDaLinha: quando mais de uma vale, a de cima pinta a linha.
export const CORES_DAS_LINHAS = [
  { cor: COR_LINHA_CANCELADA, nome: 'Cancelada', descricao: 'A carga saiu na programação definitiva (foi cancelada pela Havan).' },
  { cor: COR_LINHA_GESTOR_AVISADO, nome: 'Gestor avisado', descricao: 'O operador já avisou o gestor do motorista pelo WhatsApp.' },
  { cor: COR_LINHA_PROTOCOLO, nome: 'Protocolo', descricao: 'Carga marcada como protocolo.' },
  { cor: COR_LINHA_TERCEIRO, nome: 'Terceiro', descricao: 'Carga liberada para terceiro.' },
  { cor: COR_LINHA_VX_VALIDA, nome: 'VX válida', descricao: 'A consulta VX do motorista e dos veículos está toda ok.' },
  { cor: COR_LINHA_INCOMPLETA, nome: 'Incompleta', descricao: 'Falta o motorista ou o veículo (frota ou cavalo).' },
  { cor: '#ffffff', nome: 'Sem cor', descricao: 'Motorista e veículo informados, sem nenhuma marcação.' }
];

const DESCRICAO_STATUS_PATIO = {
  1: 'Veículo liberado para uso.',
  2: 'Veículo indisponível no momento.',
  3: 'Veículo em viagem.',
  4: 'Problemas identificados no veículo.',
  5: 'Veículo em manutenção.',
  6: 'Veículo à venda.'
};

/** Ajuda visual da tela: o que cada cor de linha e cada tag de status da Rabbot quer dizer. */
const ProgramacaoHavanLegendaModal = ({ show, onClose }) => (
  <Modal show={show} onHide={onClose} centered size="lg" animation={false}>
    <Modal.Header closeButton>
      <Modal.Title>Legenda da programação</Modal.Title>
    </Modal.Header>
    <Modal.Body>
      <Secao aria-labelledby="legenda-cores">
        <TituloSecao id="legenda-cores">Cores das linhas</TituloSecao>
        <Dica>Quando mais de uma situação vale para a carga, a cor que aparece é a mais alta da lista.</Dica>
        <Lista>
          {CORES_DAS_LINHAS.map(item => (
            <Item key={item.nome}>
              <AmostraLinha aria-hidden="true" $cor={item.cor} />
              <Nome>{item.nome}</Nome>
              <Descricao>{item.descricao}</Descricao>
            </Item>
          ))}
          <Item>
            <SeloForaTms aria-hidden="true">fora do TMS</SeloForaTms>
            <Nome>Fora do TMS</Nome>
            <Descricao>Selo laranja ao lado do motorista ou da placa: o cadastro ainda não existe no TMS.</Descricao>
          </Item>
        </Lista>
      </Secao>

      <Secao aria-labelledby="legenda-rabbot">
        <TituloSecao id="legenda-rabbot">Status da Rabbot</TituloSecao>
        <Dica>Ícone na busca de veículo (lápis da frota, cavalo e carretas), vindo da Gestão de Frotas. Passe o mouse para ver o nome. Sem ícone: o veículo não tem status.</Dica>
        <Lista>
          {Object.entries(STATUS_PATIO).map(([codigo, config]) => (
            <Item key={codigo}>
              <CelulaTag><TagStatusPatio status={Number(codigo)} /></CelulaTag>
              <Nome>{config.rotulo}</Nome>
              <Descricao>{DESCRICAO_STATUS_PATIO[codigo]}</Descricao>
            </Item>
          ))}
        </Lista>
      </Secao>

      <Secao aria-labelledby="legenda-plotagem">
        <TituloSecao id="legenda-plotagem">Plotagem do veículo</TituloSecao>
        <Dica>Selo na busca de veículo (lápis da frota, cavalo e carretas), vindo do cadastro do veículo no TMS. Sem selo: plotagem não informada no TMS.</Dica>
        <Lista>
          {Object.entries(PLOTAGENS).map(([plotagem, config]) => (
            <ItemTag key={plotagem}>
              <CelulaTag><SeloPlotagem plotagem={plotagem} /></CelulaTag>
              <Descricao>{config.nome}</Descricao>
            </ItemTag>
          ))}
        </Lista>
      </Secao>

      <Secao aria-labelledby="legenda-carroceria">
        <TituloSecao id="legenda-carroceria">Carroceria da carreta</TituloSecao>
        <Dica>Selo na busca das carretas, vindo do cadastro do veículo no TMS. Sem selo: o TMS não diz se é sider ou baú.</Dica>
        <Lista>
          {Object.entries(CARROCERIAS).map(([carroceria, config]) => (
            <ItemTag key={carroceria}>
              <CelulaTag><SeloCarroceria carroceria={carroceria} /></CelulaTag>
              <Descricao>{config.descricao}</Descricao>
            </ItemTag>
          ))}
        </Lista>
      </Secao>

      <Secao aria-labelledby="legenda-rastreador">
        <TituloSecao id="legenda-rastreador">Rastreador</TituloSecao>
        <Dica>Selo na busca de veículo com o equipamento de rastreamento cadastrado no veículo. Sem selo: nenhum equipamento cadastrado.</Dica>
        <GradeSelos>
          {TECNOLOGIAS_COM_LOGO.map(tecnologia => (
            <SeloComNome key={tecnologia}>
              <SeloTecnologia tecnologia={tecnologia} />
              <span>{tecnologia}</span>
            </SeloComNome>
          ))}
        </GradeSelos>
      </Secao>
    </Modal.Body>
    <Modal.Footer>
      <Button type="button" variant="secondary" onClick={onClose}>Fechar</Button>
    </Modal.Footer>
  </Modal>
);

ProgramacaoHavanLegendaModal.propTypes = {
  show: PropTypes.bool,
  onClose: PropTypes.func.isRequired
};

ProgramacaoHavanLegendaModal.defaultProps = {
  show: false
};

export default ProgramacaoHavanLegendaModal;

const Secao = styled.section`& + & { margin-top: 18px; }`;
const TituloSecao = styled.h6`margin: 0 0 4px; color: #163455; font-size: 12px; font-weight: 700; text-transform: uppercase;`;
const Dica = styled.p`margin: 0 0 8px; color: #526275; font-size: 12px;`;
const Lista = styled.ul`margin: 0; padding: 0; list-style: none; border: 1px solid #e3e8ef; border-radius: 6px; overflow: hidden;`;
const Item = styled.li`
  display: grid;
  grid-template-columns: 92px 150px 1fr;
  align-items: center;
  gap: 12px;
  padding: 7px 12px;
  font-size: 13px;
  & + & { border-top: 1px solid #eef2f6; }
  @media (max-width: 576px) { grid-template-columns: 72px 1fr; .descricao { grid-column: 1 / -1; } }
`;
const ItemTag = styled(Item)`
  grid-template-columns: 92px 1fr;
  @media (max-width: 576px) { grid-template-columns: 92px 1fr; .descricao { grid-column: auto; } }
`;
const AmostraLinha = styled.span`
  display: block;
  width: 72px;
  height: 18px;
  border: 1px solid rgba(15, 23, 42, 0.15);
  border-radius: 3px;
  background: ${({ $cor }) => $cor};
`;
const CelulaTag = styled.span`display: inline-flex;`;
const GradeSelos = styled.ul`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(150px, 1fr));
  gap: 8px;
  margin: 0;
  padding: 10px 12px;
  list-style: none;
  border: 1px solid #e3e8ef;
  border-radius: 6px;
`;
const SeloComNome = styled.li`display: flex; align-items: center; gap: 8px; color: #24344d; font-size: 13px;`;
const Nome = styled.strong`color: #24344d; font-weight: 600;`;
const Descricao = styled.span.attrs({ className: 'descricao' })`color: #526275;`;
const SeloForaTms = styled.span`
  justify-self: start;
  padding: 1px 6px;
  border: 1px solid #fdba74;
  border-radius: 999px;
  background: #fff7ed;
  color: ${COR_FORA_TMS};
  font-size: 10px;
  font-weight: 700;
  white-space: nowrap;
`;
